import React from 'react';
import { c2w_figma } from '../image_index';

export const sections = [
    {
        id: 'c2w_empathize',
        title: 'Empathizing',
        content: (
            <p>
                Andrea Bowers' neon installation <i>Education Should Be Free</i> addresses the growing financial burden of higher education. To make this message tangible, I identified our primary audience as museum visitors and students who wanted concrete context on how higher education costs have scaled over time relative to standard economic metrics.
            </p>
        )
    },
    {
        id: 'c2w_define',
        title: 'Problem Statement',
        content: (
            <div>
                <p>
                    Raw numbers often fail to convey real-world impact. Viewers needed an intuitive, data-driven experience to instantly grasp the disparity between rising UC tuition and key economic benchmarks (such as minimum wage and the Consumer Price Index).
                </p>
                <h2>Design Goal</h2>
                <p>
                    Translate static economic statistics into an interactive, mobile-first visual experience that mirrors the aesthetic of the neon artwork while maintaining high data clarity.
                </p>
            </div>
        )
    },
    {
        id: 'c2w_ideate',
        title: 'Design',
        content: (
            <div>
                <p>
                    Initial low-fidelity exploration focused on translating the glowing, vibrant aesthetic of neon lighting into a digital mobile UI using custom vector graphics and modal pop-ups.
                </p>
                <img src={c2w_figma} alt='Draft of initial wireframes and design exploration' />

                <h2>Key Feature Set</h2>
                <ul>
                    <li><b>Custom Neon Visuals:</b> Vector illustrations capturing the artwork's identity.</li>
                    <li><b>Benchmark Selection:</b> Interactive comparisons against Median US Income, CA Minimum Wage, S&P 500, and CPI.</li>
                    <li><b>Data Visualization:</b> Animated comparison graphs covering resident vs. non-resident UC tuition.</li>
                    <li><b>Contextual Insights:</b> "Learn More" sections breaking down why each economic metric is relevant.</li>
                </ul>

                <h2>User Feedback & UX Refinements</h2>
                <p>
                    Critiques with instruction staff revealed key usability bottlenecks:
                </p>
                <ul>
                    <li><b>Reducing Friction:</b> Overlaid pop-ups felt intrusive on mobile viewports; I eliminated them in favor of dedicated, non-modal detail screens.</li>
                    <li><b>Improving Affordance:</b> Added explicit visual cues and instructional microcopy to clarify interactive custom icons.</li>
                    <li><b>Information Architecture:</b> Reordered graph displays to prioritize immediate visual impact before diving into narrative context.</li>
                </ul>
            </div>
        )
    },
    {
        id: 'c2w_prototype',
        title: 'Prototype',
        content: (
            <div>
                <p>
                    I brought the high-fidelity design to life using <b>React</b> for state management and component structure, alongside <b>D3.js</b> for animated, responsive data visualizations.
                </p>
                <div className='case_study_section_imageblock'>
                    <iframe
                        frameBorder='0'
                        title='Compared to What? Interactive Prototype'
                        width='372px'
                        height='650px'
                        src='https://finalmindymohr.ecs162instruct.repl.co/'
                    />
                </div>
            </div>
        )
    },
    {
        id: 'c2w_test',
        title: 'Takeaways',
        content: (
            <div>
                <p>
                    This project marked my first end-to-end integration of technical frontend libraries (React & D3) with custom UI/UX prototyping. Balancing aesthetic fidelity with accessible data presentation taught me how to handle complex DOM manipulation inside React lifecycle components.
                </p>
                <h2>Future Considerations</h2>
                <ul>
                    <li><b>Usability Testing:</b> Conduct task-based testing with museum-goers to evaluate graph comprehension times.</li>
                    <li><b>Accessibility (a11y):</b> Enhance color contrast ratios for the neon theme to meet WCAG AA standards and add ARIA labels for D3 data points.</li>
                </ul>
            </div>
        )
    }
];